class SizeGuideDrawer extends HTMLElement {
  constructor() {
    super();
    this.cache = new Map();
    this.activeElement = null;
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
  }

  connectedCallback() {
    const existing = document.querySelector('size-guide-drawer');
    if (existing && existing !== this) {
      this.remove();
      return;
    }

    // Keep fixed positioning reliable (escape product column overflow/transforms)
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this.overlay = this.querySelector('#SizeGuideDrawer-Overlay');
    this.inner = this.querySelector('.size-guide-drawer__inner');
    this.content = this.querySelector('[data-size-guide-content]');
    this.loading = this.querySelector('[data-size-guide-loading]');
    this.titleEl = this.querySelector('#SizeGuideDrawer-Title');
    this.closeBtn = this.querySelector('[data-size-guide-close]');

    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('click', this.onTriggerClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onTriggerClick);
    document.removeEventListener('keyup', this.onKeyUp);
  }

  onTriggerClick(event) {
    const trigger = event.target.closest('[data-size-guide-open]');
    if (!trigger) return;

    event.preventDefault();
    this.open(trigger);
  }

  onKeyUp(event) {
    if (event.code === 'Escape') this.close();
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.activeElement = triggeredBy;
      if (this.titleEl) {
        const label = triggeredBy.getAttribute('data-size-guide-label') || triggeredBy.textContent;
        this.titleEl.textContent = (label || 'Size guide').trim();
      }
    }

    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keyup', this.onKeyUp);

    this.addEventListener(
      'transitionend',
      () => {
        const focusEl = this.closeBtn || this.inner;
        if (typeof trapFocus === 'function') {
          trapFocus(this.querySelector('#SizeGuideDrawer') || this, focusEl);
        } else {
          focusEl?.focus();
        }
      },
      { once: true }
    );

    const url = triggeredBy?.getAttribute('href') || triggeredBy?.dataset?.sizeGuideUrl;
    if (url) this.loadContent(url);
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keyup', this.onKeyUp);

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    } else {
      this.activeElement?.focus?.();
    }
  }

  buildFetchUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      if (!parsed.searchParams.has('view')) {
        parsed.searchParams.set('view', 'sizechart');
      }
      return parsed.toString();
    } catch (e) {
      const join = url.includes('?') ? '&' : '?';
      return url.includes('view=') ? url : `${url}${join}view=sizechart`;
    }
  }

  async loadContent(url) {
    if (!this.content) return;

    const fetchUrl = this.buildFetchUrl(url);
    if (this.cache.has(fetchUrl)) {
      this.renderHtml(this.cache.get(fetchUrl));
      return;
    }

    this.setLoading(true);

    try {
      const response = await fetch(fetchUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`Size guide fetch failed: ${response.status}`);

      const html = await response.text();
      this.cache.set(fetchUrl, html);
      this.renderHtml(html);
    } catch (error) {
      console.error(error);
      this.content.innerHTML =
        '<p class="size-guide-drawer__error">Unable to load size guide. <a href="' +
        String(url).replace(/"/g, '&quot;') +
        '" target="_blank" rel="noopener">Open in new tab</a>.</p>';
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (!this.loading) return;
    this.loading.hidden = !isLoading;
  }

  wrapTables() {
    this.content?.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList.contains('size-guide-drawer__table-scroll')) return;
      const wrap = document.createElement('div');
      wrap.className = 'size-guide-drawer__table-scroll';
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  }

  renderHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Prefer chart body; fall back to main / layout-none page body
    const source =
      doc.querySelector('.size-chart-modal-info') ||
      doc.querySelector('[data-size-guide-content]') ||
      doc.querySelector('#MainContent') ||
      doc.querySelector('main') ||
      doc.body;

    // Scripts from AJAX HTML do not execute via innerHTML; unit toggle is loaded separately
    source.querySelectorAll('script').forEach((el) => el.remove());

    const loadingMarkup = this.loading ? this.loading.outerHTML : '';
    this.content.innerHTML = loadingMarkup + source.innerHTML;
    this.loading = this.content.querySelector('[data-size-guide-loading]');
    this.setLoading(false);
    this.wrapTables();

    if (typeof window.__initSizeChart === 'function') {
      window.__initSizeChart();
    }
  }
}

if (!customElements.get('size-guide-drawer')) {
  customElements.define('size-guide-drawer', SizeGuideDrawer);
}
